// src/App.tsx
import React, { useState } from 'react';
import { Layout } from './components/layout/Layout';
import { InputPanel } from './components/ui/InputPanel';
import { OutputPanel } from './components/ui/OutputPanel';
import { LoginModal } from './components/auth/LoginModal';
import { HistoryPanel } from './components/history/HistoryPanel';
import { UpgradeModal } from './components/payment/UpgradeModal';
import { ContactModal } from './components/modals/ContactModal';
import { PrivacyModal } from './components/modals/PrivacyModal';
import { TermsModal } from './components/modals/TermsModal';
import { AboutModal } from './components/modals/AboutModal';

import { useAuth } from './hooks/useAuth';
import { useUsageTracking } from './hooks/useUsageTracking';
import { useHistory } from './hooks/useHistory';

import { apiService } from './services/api';
import { copyToClipboard, downloadText } from './utils/helpers';
import { APP_CONFIG } from './utils/constants';

import type { HistoryItem } from './types/usage';

const App: React.FC = () => {
  // Auth state
  const {
    user,
    loading: authLoading,
    error: authError,
    isAuthenticated,
    signInWithGoogle,
    signInWithEmail,
    signUpWithEmail,
    signOut,
    getIdToken
  } = useAuth();

  // Usage tracking
  const { remainingCalls } = useUsageTracking(isAuthenticated);

  // History management
  const { history, addToHistory, clearHistory, removeHistoryItem, updateHistoryRating } = useHistory();

  // UI state
  const [inputText, setInputText] = useState('');
  const [outputText, setOutputText] = useState('');
  const [isDemoSelected, setIsDemoSelected] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showHistoryPanel, setShowHistoryPanel] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [showContactModal, setShowContactModal] = useState(false);
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [showAboutModal, setShowAboutModal] = useState(false);
  const [currentHistoryId, setCurrentHistoryId] = useState<string | null>(null);
  const [currentOutputRating, setCurrentOutputRating] = useState<'thumbs_up' | 'thumbs_down' | null>(null);

  // Handle demo selection
  const handleDemoSelect = (input: string, output: string) => {
    setInputText(input);
    setOutputText(output);
    setIsDemoSelected(true);
  };

  // Handle clear example
  const handleClearExample = () => {
    setInputText('');
    setOutputText('');
    setIsDemoSelected(false);
  };

  // Handle AI request submission
  const handleSubmit = async () => {
    if (!inputText.trim()) return;

    // Check if user is authenticated (logged in, not anonymous)
    if (!isAuthenticated) {
      setShowLoginModal(true);
      return;
    }

    setIsLoading(true);
    setCurrentOutputRating(null); // Reset rating when new request starts
    
    try {
      // Get auth token for authenticated user
      const authToken = await getIdToken();
      
      // Prepare request
      const request = {
        prompt: inputText
      };

      // Make API call
      const response = await apiService.callAI(request, authToken || undefined);
      
      // Set output
      setOutputText(response.response);

      // Add to history and track current item
      const historyId = Date.now().toString();
      addToHistory(inputText, response.response, response.model, 'no_rating', historyId);
      setCurrentHistoryId(historyId);

    } catch (error) {
      if (error instanceof Error) {
        if (error.message === 'RATE_LIMIT_EXCEEDED') {
          setShowLoginModal(true);
        } else {
          setOutputText(`Error: ${error.message}`);
        }
      } else {
        setOutputText('Error: An unexpected error occurred');
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Handle copy to clipboard
  const handleCopy = async () => {
    const success = await copyToClipboard(outputText);
    if (success) {
      // You could add a toast notification here
      console.log('Copied to clipboard');
    }
  };

  // Handle download
  const handleDownload = () => {
    downloadText(outputText, `dairy-translate-${Date.now()}.txt`);
  };

  // Handle history selection
  const handleSelectHistory = (item: HistoryItem) => {
    setInputText(item.input);
    setOutputText(item.output);
    setCurrentHistoryId(item.id);
    setCurrentOutputRating(
      item.rating === 'no_rating' || !item.rating ? null : item.rating
    );
  };

  // Handle upgrade (placeholder for Stripe integration)
  const handleUpgrade = async (tierId: string) => {
    console.log('Upgrading to tier:', tierId);
    // This will be implemented when Stripe is integrated
    // For now, just close the modal
    setShowUpgradeModal(false);
  };

  // Handle feedback
  const handleThumbsUp = async () => {
    // Toggle rating if already thumbs up, otherwise set to thumbs up
    const newRating = currentOutputRating === 'thumbs_up' ? null : 'thumbs_up';
    
    // Log positive feedback and potentially send to API
    console.log('Positive feedback for result:', { inputText, outputText });
    // TODO: Send feedback to API when backend is ready
    // await apiService.sendFeedback({ type: 'positive', input: inputText, output: outputText });
    
    // Update history rating
    if (currentHistoryId) {
      updateHistoryRating(currentHistoryId, newRating === null ? 'no_rating' : newRating);
    }
    
    // Set current output rating for UI highlighting
    setCurrentOutputRating(newRating);
  };

  const handleThumbsDown = async () => {
    // Toggle rating if already thumbs down, otherwise set to thumbs down
    const newRating = currentOutputRating === 'thumbs_down' ? null : 'thumbs_down';
    
    // Log negative feedback and potentially send to API
    console.log('Negative feedback for result:', { inputText, outputText });
    // TODO: Send feedback to API when backend is ready
    // await apiService.sendFeedback({ type: 'negative', input: inputText, output: outputText });
    
    // Update history rating
    if (currentHistoryId) {
      updateHistoryRating(currentHistoryId, newRating === null ? 'no_rating' : newRating);
    }
    
    // Set current output rating for UI highlighting
    setCurrentOutputRating(newRating);
  };

  // Show loading screen while auth is initializing
  if (authLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <Layout
      user={user}
      isAuthenticated={isAuthenticated}
      remainingCalls={remainingCalls}
      onShowHistory={() => setShowHistoryPanel(true)}
      onShowLogin={() => setShowLoginModal(true)}
      onSignOut={signOut}
      onShowContact={() => setShowContactModal(true)}
      onShowPrivacy={() => setShowPrivacyModal(true)}
      onShowTerms={() => setShowTermsModal(true)}
      onShowAbout={() => setShowAboutModal(true)}
    >
      {/* Main Content Area */}
      <div className="flex-1 flex flex-col lg:flex-row min-h-0">
        <InputPanel
          value={inputText}
          onChange={setInputText}
          onSubmit={handleSubmit}
          onDemoSelect={handleDemoSelect}
          onClearExample={handleClearExample}
          isDemoSelected={isDemoSelected}
          isLoading={isLoading}
          disabled={authLoading}
          maxLength={APP_CONFIG.MAX_INPUT_LENGTH}
        />

        <OutputPanel
          value={outputText}
          isLoading={isLoading}
          onCopy={outputText ? handleCopy : undefined}
          onDownload={outputText ? handleDownload : undefined}
          onShowHistory={() => setShowHistoryPanel(true)}
          onThumbsUp={outputText ? handleThumbsUp : undefined}
          onThumbsDown={outputText ? handleThumbsDown : undefined}
          currentRating={currentOutputRating}
        />
      </div>

      {/* Update Message */}
      <div className="text-center py-4">
        <p className="text-xs text-gray-400">Last update: 8/19/2026</p>
        <p className="text-xs text-gray-400">Now Mobile Friendly</p>
      </div>

      {/* Modals */}
      <LoginModal
        isOpen={showLoginModal}
        onClose={() => setShowLoginModal(false)}
        onGoogleSignIn={signInWithGoogle}
        onEmailSignIn={signInWithEmail}
        onEmailSignUp={signUpWithEmail}
        loading={authLoading}
        error={authError}
      />

      <HistoryPanel
        isOpen={showHistoryPanel}
        onClose={() => setShowHistoryPanel(false)}
        history={history}
        onSelectHistory={handleSelectHistory}
        onDeleteHistory={removeHistoryItem}
        onClearHistory={clearHistory}
      />

      <UpgradeModal
        isOpen={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        onUpgrade={handleUpgrade}
      />
      
      <ContactModal
        isOpen={showContactModal}
        onClose={() => setShowContactModal(false)}
      />

      <PrivacyModal
        isOpen={showPrivacyModal}
        onClose={() => setShowPrivacyModal(false)}
      />

      <TermsModal
        isOpen={showTermsModal}
        onClose={() => setShowTermsModal(false)}
      />

      <AboutModal
        isOpen={showAboutModal}
        onClose={() => setShowAboutModal(false)}
      />
    </Layout>
  );
};

export default App;